import { Box, Stack } from '@mui/material';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import ListEditor, { ListItem } from '../list-editor';
import { FormProps } from '../helpers';
import TextInput from '../text-input';
import { ProxyIpRangeConfig } from '@/lib/models/connector/proxy-connector.model';
import { fromListItem, toListItems } from './helpers';
import { isValidCidr } from '@/lib/helpers';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import FlatButton from '@/components/flat-button';
import useFeature from '@/hooks/use-feature.hook';
import Text from '@/components/text';

interface Props extends FormProps {
  value: ProxyIpRangeConfig[] | undefined;
  onChange: (value: ProxyIpRangeConfig[]) => void;
}

const ProxyIpRangeListEditor: FC<Props> = ({
  value = [],
  onChange,
  name,
  label,
  readonly = false,
  required = false,
  errors: allErrors,
}) => {
  const items = toListItems(value);
  const { hasAssume } = useFeature();

  // @todo move this into ProxyConnectorModel
  const errors: string[] = useMemo(
    () => ((allErrors?.sourceCIDRs as { cidr: string }[]) || []).map(({ cidr }) => cidr),
    [allErrors]
  );

  const handleChange = useCallback(
    (item: ListItem, editIndex: number) => {
      const config = fromListItem(item);
      const updated = [...(value ?? [])];
      if (editIndex === -1) {
        updated.unshift(config);
      } else {
        updated[editIndex] = config;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  const handleRemove = useCallback(
    (_item: ListItem, index: number) => {
      const updated = [...value];
      updated.splice(index, 1);
      onChange(updated);
    },
    [value, onChange]
  );

  const handleActivate = useCallback(
    (item: ListItem) => {
      const configIndex = value.findIndex(({ cidr }) => cidr === item.value);
      if (configIndex < 0) {
        return;
      }

      const updated = [...value];
      updated[configIndex] = { ...updated[configIndex], status: 'active' };
      onChange(updated);
    },
    [value, onChange]
  );

  const handleItemView = useCallback(
    (item: ListItem, index: number): ReactNode => {
      const cidr = String(item.value ?? '');
      const status = (item.meta?.status as string) ?? 'pending';
      const error = errors[index];
      const hasError = !!error;
      const showActivate = hasAssume && status === 'pending' && !hasError;

      return (
        <Box width="100%">
          <Stack direction="row" alignItems="center" gap={1} justifyContent="flex-start">
            <Text size="small">{cidr}</Text>
            <StatusChip size="x-small" value={status as ChipStatus} />
            {showActivate && (
              <FlatButton
                size="x-small"
                color="link"
                label="ACTIVATE"
                onClick={() => handleActivate(item)}
              />
            )}
          </Stack>
          {hasError && (
            <Box width="100%" mt={1}>
              <Text color="error" size="x-small">
                {error}
              </Text>
            </Box>
          )}
        </Box>
      );
    },
    [handleActivate, hasAssume, errors]
  );

  const itemIsValid = useCallback((item: ListItem): boolean => {
    const cidr = String(item.value ?? '').trim();
    if (!cidr) {
      return false;
    }
    return isValidCidr(cidr);
  }, []);

  const handleInput = useCallback(
    (setModItem: (item: ListItem) => void, modItem: ListItem): ReactNode => {
      const cidr = String(modItem.value ?? '');

      return (
        <Box flexGrow={1} minWidth={0}>
          <TextInput
            name="proxy-ip-range-cidr"
            value={cidr}
            onChange={(newCidr) => setModItem({ ...modItem, value: newCidr })}
            size="small"
            placeholder="e.g. 50.10.90.0/24"
          />
        </Box>
      );
    },
    []
  );

  return (
    <ListEditor
      name={name}
      label={label}
      required={required}
      readonly={readonly}
      items={items}
      onChange={handleChange}
      onRemove={handleRemove}
      onInput={handleInput}
      onItemView={handleItemView}
      itemIsValid={itemIsValid}
      height={180}
    />
  );
};

export default ProxyIpRangeListEditor;
